import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { getPaymentResult } from '../../api/endpoints/payment';
import { getShoppingCart } from '../../api/endpoints/shoppingCart';
import { createInsurancePolicy } from '../../api/endpoints/insurance';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { PaymentResultStatus } from '../../api/types/payment';
import { InsurancePolicyButton } from '../../components/reservation/InsurancePolicyButton';
import { AirInsuranceReturnPanel } from './AirInsuranceReturnPanel';

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 20;

/** 3DS uçuş ödemesi bankaya giderken bırakılan "dönüşte sigortayı sun" bayrağı
 *  (`flowConfig.ts` `runPayment`); `air:pendingInsuranceLeg` ise sigorta ödemesi de 3DS
 *  ise bırakılır (`AirInsuranceReturnPanel`) — dönüşte yalnız `CreatePolicy` kalır. */
const PENDING_KEY = (cartId: string) => `air:pendingInsurance:${cartId}`;
const LEG_KEY = (cartId: string) => `air:pendingInsuranceLeg:${cartId}`;

function readFlag(key: string): string | null {
  try {
    return sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
}
function clearFlag(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* özel sekme / storage kapalı */
  }
}

/** Sepette dolu olan booking dizisinden ürünün rezervasyon listesi yolunu çıkar.
 *  Belirlenemezse (dizi boş/erişilemez) `/air/reservations`'a düşer. */
function reservationsPathFromCart(cart: ShoppingCart | null | undefined): string {
  const has = (arr: unknown) => Array.isArray(arr) && arr.length > 0;
  if (!cart) return '/air/reservations';
  if (has(cart.hotelBookings)) return '/hotel/reservations';
  if (has(cart.busBookings)) return '/bus/reservations';
  if (has(cart.transferBookings)) return '/transfer/reservations';
  if (has(cart.rentCarBookings)) return '/rent-car/reservations';
  return '/air/reservations';
}

/** Biletlenmiş bir uçuş sepetinden detay sayfası yolu (`Entry/Find` PNR ya da bilet no ile).
 *  Yalnız Air için — diğer ürünlerde liste yeterli; PNR/bilet yoksa `null`. */
function airDetailPathFromCart(cart: ShoppingCart | null | undefined): string | null {
  const booking = (cart?.airBookings ?? [])[0] as
    | { bookingCode?: string; passengerBreakdowns?: { ticketNumber?: string }[] }
    | undefined;
  if (!booking) return null;
  const pnr = booking.bookingCode;
  if (pnr) return `/air/reservations/${encodeURIComponent(pnr)}?by=pnr`;
  const ticket = booking.passengerBreakdowns?.find((pb) => pb.ticketNumber)?.ticketNumber;
  if (ticket) return `/air/reservations/${encodeURIComponent(ticket)}?by=ticket`;
  return null;
}

/**
 * Landing page for the 3D Secure return leg. The bank redirects the browser here (the
 * `returnUrl` `PaymentForm` builds is `/payment/return?cart=<id>`); the in-memory
 * booking flow is gone by now, so this page only confirms the payment outcome and
 * routes the user onward. Polls `Payment/GetPaymentResult`; if that endpoint is absent
 * it falls back to inspecting the ShoppingCart's paid/finalized flags. The cart is also
 * fetched to route the "Rezervasyon Listesi" button to the right product.
 *
 * Uçuşa bağlı sigorta (Tamamlıyo) — 3DS uçuş ödemesinde flow state bankada kaybolduğu
 * için sigorta burada tamamlanır: uçuş başarılıysa ve `air:pendingInsurance` bayrağı varsa
 * `AirInsuranceReturnPanel` (`CreateOffer` → ikinci `MakePayment` → `CreatePolicy`).
 * Sigorta ödemesi de 3DS ise ikinci banka turundan sonra `air:pendingInsuranceLeg` ile
 * geri dönülür ve burada yalnız `CreatePolicy` çağrılır.
 */
export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const cartId = params.get('cart') ?? params.get('shoppingCartId') ?? '';

  // İlk render'da bayrakları oku — banka aynı sekmede döndüğü için korunmuş olurlar.
  const [insuranceLegTeklifId] = useState<string | null>(() =>
    cartId ? readFlag(LEG_KEY(cartId)) : null
  );
  const [wantsInsurancePrompt] = useState<boolean>(() =>
    cartId ? readFlag(PENDING_KEY(cartId)) === '1' : false
  );

  const [status, setStatus] = useState<PaymentResultStatus | 'unknown' | 'insurance-done'>('pending');
  const [message, setMessage] = useState<string | null>(null);
  const [reservationsPath, setReservationsPath] = useState('/air/reservations');
  const [detailPath, setDetailPath] = useState<string | null>(null);
  const [insuranceTeklifId, setInsuranceTeklifId] = useState<string | null>(null);
  const [insurancePromptDismissed, setInsurancePromptDismissed] = useState(false);
  const attempts = useRef(0);

  // Ürün yolunu + (varsa) biletin detay yolunu belirle (poll'dan bağımsız, hata yutulur).
  // Ödeme sonuçlanınca (success / insurance-done) tekrar çek — ilk çekimde finalize/PNR
  // henüz gelmemiş olabilir.
  const settled = status === 'success' || status === 'insurance-done' || status === 'failed';
  useEffect(() => {
    if (!cartId) return;
    let stopped = false;
    void getShoppingCart(cartId)
      .then((res) => {
        if (stopped) return;
        setReservationsPath(reservationsPathFromCart(res.shoppingCart));
        const dp = airDetailPathFromCart(res.shoppingCart);
        if (dp) setDetailPath(dp);
      })
      .catch(() => undefined);
    return () => {
      stopped = true;
    };
  }, [cartId, settled]);

  useEffect(() => {
    if (!cartId) {
      setStatus('unknown');
      return;
    }
    let stopped = false;

    const resolveViaCart = async (): Promise<'success' | 'pending' | 'unknown'> => {
      try {
        const res = await getShoppingCart(cartId);
        const cart = res.shoppingCart as
          | { isFinalized?: boolean; shoppingSummary?: { paid?: boolean } }
          | undefined;
        if (cart?.isFinalized || cart?.shoppingSummary?.paid) return 'success';
        return 'pending';
      } catch {
        return 'unknown';
      }
    };

    const pollStatus = async (): Promise<PaymentResultStatus | 'unknown'> => {
      try {
        const res = await getPaymentResult(cartId);
        if (res.message) setMessage(res.message);
        return res.status ?? 'pending';
      } catch {
        return resolveViaCart();
      }
    };

    // Sigorta bacağı 3DS dönüşü: ödeme başarılıysa yalnız CreatePolicy kaldı.
    const finishInsuranceLeg = async (teklifId: string) => {
      try {
        const fresh = (await getShoppingCart(cartId)).shoppingCart;
        if (fresh) await createInsurancePolicy({ teklifId, shoppingCart: fresh });
      } catch {
        /* poliçe kesilemedi — uçuş + sigorta ödemesi alındı; rezervasyon detayından denenebilir */
      }
      clearFlag(LEG_KEY(cartId));
      clearFlag(PENDING_KEY(cartId));
      if (!stopped) {
        setInsuranceTeklifId(teklifId);
        setStatus('insurance-done');
      }
    };

    const dropInsuranceFlags = () => {
      clearFlag(LEG_KEY(cartId));
      clearFlag(PENDING_KEY(cartId));
    };

    const tick = async () => {
      if (stopped) return;
      attempts.current += 1;
      const next = await pollStatus();
      if (stopped) return;

      if (next === 'success') {
        if (insuranceLegTeklifId) void finishInsuranceLeg(insuranceLegTeklifId);
        else setStatus('success');
        return;
      }
      if (next === 'failed') {
        if (insuranceLegTeklifId) {
          // Sigorta ödemesi başarısız — uçuş biletli kalır, düz başarıya düş.
          dropInsuranceFlags();
          setMessage('Sigorta ödemesi tamamlanamadı; uçuş biletiniz geçerli.');
          setStatus('success');
        } else {
          setStatus('failed');
        }
        return;
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        if (insuranceLegTeklifId) {
          dropInsuranceFlags();
          setStatus('success');
        } else {
          setStatus('unknown');
        }
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    void tick();
    return () => {
      stopped = true;
    };
  }, [cartId, insuranceLegTeklifId]);

  const showInsurancePrompt =
    status === 'success' &&
    wantsInsurancePrompt &&
    !insurancePromptDismissed &&
    !insuranceLegTeklifId &&
    !!cartId;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        {status === 'pending' && (
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant="h6">Ödeme sonucu doğrulanıyor…</Typography>
            <Typography variant="body2" color="text.secondary">
              Bankadan dönüş bekleniyor. Bu sayfayı kapatmayın.
            </Typography>
          </Stack>
        )}

        {showInsurancePrompt && (
          <Stack spacing={2}>
            <Stack spacing={1} sx={{ alignItems: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6">Uçuş ödemesi başarılı</Typography>
            </Stack>
            <AirInsuranceReturnPanel
              cartId={cartId}
              onDone={(id) => {
                setInsuranceTeklifId(id);
                setStatus('insurance-done');
              }}
              onSkip={() => setInsurancePromptDismissed(true)}
            />
          </Stack>
        )}

        {status === 'success' && !showInsurancePrompt && (
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6">Ödeme başarılı</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              İşleminiz tamamlandı.
              {detailPath ? ' Rezervasyon detayınıza gidebilirsiniz.' : ' Rezervasyon detayına ilgili listeden ulaşabilirsiniz.'}
            </Typography>
            {insuranceLegTeklifId && message && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                {message}
              </Alert>
            )}
            <Button component={RouterLink} to={detailPath ?? reservationsPath} variant="contained">
              {detailPath ? 'Rezervasyonu Aç' : 'Rezervasyon Listesine Git'}
            </Button>
          </Stack>
        )}

        {status === 'insurance-done' && (
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6">Uçuş ve sigorta ödemeniz tamamlandı</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Poliçe belgenize aşağıdan ulaşabilir, rezervasyon detayından tekrar açabilirsiniz.
            </Typography>
            {insuranceTeklifId && <InsurancePolicyButton teklifId={insuranceTeklifId} />}
            <Button component={RouterLink} to={detailPath ?? reservationsPath} variant="contained">
              {detailPath ? 'Rezervasyonu Aç' : 'Rezervasyon Listesine Git'}
            </Button>
          </Stack>
        )}

        {status === 'failed' && (
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 56 }} />
            <Typography variant="h6">Ödeme tamamlanamadı</Typography>
            {message && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {message}
              </Alert>
            )}
            <Button component={RouterLink} to="/" variant="outlined">
              Anasayfaya Dön
            </Button>
          </Stack>
        )}

        {status === 'unknown' && (
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <ErrorOutlineIcon color="warning" sx={{ fontSize: 56 }} />
            <Typography variant="h6">Ödeme durumu belirlenemedi</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Rezervasyon listenizden işlemin durumunu kontrol edin; tutar çekildiyse işlem büyük olasılıkla
              tamamlanmıştır.
            </Typography>
            <Button component={RouterLink} to={reservationsPath} variant="outlined">
              Rezervasyon Listesine Git
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
